import pdfMake from 'pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import handleHospital from "@/services/modules/hospital";
import useDate from '@/services/utils/day';
import { chunkArray, formatDateToDdMmYy, joinArrayOfString } from '@/services/utils/global';
import { startCase, head, last } from 'lodash';

pdfMake.fonts = {
    SulaimanLipi: {
        normal: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff',
        bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
        italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
        bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf'
    }
}

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const pdfPrinter = () => {
    const { formatDate } = useDate();
    const { calculateAge } = handleHospital();

    async function measureAndUpdateFooter(docDefinition) {
        return new Promise(resolve => {

            let pageInnerHeight = 0;
            let contentTop = 0;

            docDefinition.pageBreakBefore = (currentNode) => {
                if (! currentNode) return false;

                // If this is the footerSection, update the text
                if (currentNode.id === 'footerSection') {
                    pageInnerHeight = currentNode.startPosition.pageInnerHeight;
                    contentTop = currentNode.startPosition.top;
                }

                return false;
            };

            pdfMake.createPdf(docDefinition).getBuffer(() => {
                resolve({ pageInnerHeight, contentTop });
            });
        });
    }

    const getMargin = (item, contentTop, pageInnerHeight, pageMargins) => {
        let headerMargin = pageMargins.length > 0 ? pageMargins[1] : 100
        const contentHeight = getContentHeight(item);
        let topMargin = Math.floor(Math.ceil(pageInnerHeight + headerMargin) - Math.ceil(contentTop) - Math.ceil(contentHeight) - 16);
    
        return [0, topMargin, 0, 0];
    }

    const getContentHeight = (item) => {
        if (!item.positions || item.positions.length === 0) return 0;

        const tops = item.positions.map(p => p.top);
        const minTop = Math.min(...tops);
        const maxTop = Math.max(...tops);

        return maxTop - minTop;
    };

    const getPageMargin = (margins) => {
        if(margins) {
            return [ margins.left, margins.top, margins.right, margins.bottom ]
        }

        return [ 75, 150, 25, 100 ];
    };

    const exportToPDF = async (company, admissionInfo, dischargeNotes, formData, qrcode, userName, barcodePatient, barcodeAdmission, notesOrder, dischargeModes, treatedByDoctors, serviceDoctorOrder = null, customizationFields = []) => {
        let margins = null;

        if(customizationFields['extra_settings_margin'] && customizationFields['extra_settings_margin'].is_active) {
            margins = customizationFields['extra_settings_margin'].value
        }

        var doc = {
            pageSize: 'A4',
            pageMargins: getPageMargin(margins),
            header: getHeader(company, customizationFields, margins),
            footer: getFooter(userName, qrcode, customizationFields, margins),
            content: getContent(doc, admissionInfo, dischargeNotes, formData, barcodePatient, barcodeAdmission, notesOrder, dischargeModes, treatedByDoctors, serviceDoctorOrder),
            
            styles : {
                header: {
                    fontSize: 24,
                }
            },

            defaultStyle: {
                color: 'black',
                fontSize: 10,
                font: 'SulaimanLipi'
            },

            info: {
                title: 'DischaregeOrder'
            }
        }

        doc.content.push(getFooterTop(serviceDoctorOrder));

        const { pageInnerHeight, contentTop } = await measureAndUpdateFooter(doc);

        doc.content.forEach(item => {
            if(item.id == 'footerSection') {
                item.margin = getMargin(item, contentTop, pageInnerHeight, doc.pageMargins);
            }
        });

        const pdfDocGenerator = pdfMake.createPdf(doc);
        pdfDocGenerator.open();
    }

    const getContent = (doc, admissionInfo, dischargeNotes, formData, barcodePatient, barcodeAdmission, notesOrder, dischargeModes, treatedByDoctors, serviceDoctorOrder) => {
        return [
            {
                text: 'Discharge Certificate',
                style: 'header',
                alignment: 'center',
                bold: true,
                color: 'black',
                margin: [0, 0, 10, 0]
            },

            {
                margin: [0, 20, 0, 0],
                table: {
                    widths: ['50%', '*'],
                    body: [
                        [
                            {
                                // LEFT COLUMN
                                stack: [
                                    {
                                        svg: barcodePatient
                                    },
                                    {
                                        text: [
                                            { text: 'Patient Name: ', bold: true },
                                            { text: admissionInfo.patient.full_name }
                                        ],
                                        alignment: 'left'
                                    },
                                    {
                                        text: [
                                            { text: 'Patient ID: ', bold: true },
                                            { text: admissionInfo.patient.serial_no }
                                        ],
                                        alignment: 'left'
                                    },
                                    {
                                        text: [
                                            admissionInfo.patient.birthday
                                                ? { text: 'Age: ', bold: true }
                                                : {},
                                            admissionInfo.patient.birthday
                                                ? { text: calculateAge(admissionInfo.patient.birthday) + ' ' }
                                                : {},
                                            admissionInfo.patient.gender
                                                ? { text: '| Sex: ', bold: true }
                                                : {},
                                            admissionInfo.patient.gender
                                                ? { text: admissionInfo.patient.gender }
                                                : {}
                                        ],
                                        alignment: 'left'
                                    },
                                    {
                                        text: [
                                            { text: 'Patient Phone No: ', bold: true },
                                            { text: admissionInfo.patient.mobile_no }
                                        ],
                                        alignment: 'left'
                                    },
                                    {
                                        text: [
                                            admissionInfo.patient.full_address
                                                ? { text: 'Patient Address: ', bold: true }
                                                : {},
                                            admissionInfo.patient.full_address
                                                ? { text: admissionInfo.patient.full_address }
                                                : {}
                                        ],
                                        alignment: 'left'
                                    }
                                ]
                            },
                        
                            {
                                // RIGHT COLUMN
                                alignment: 'right',
                                stack: [
                                    {
                                        svg: barcodeAdmission
                                    },
                                    {
                                        text: [
                                            { text: 'Admission No: ', bold: true },
                                            { text: admissionInfo.admission_no }
                                        ]
                                    },
                                    {
                                        text: [
                                            { text: 'Date of Admission: ', bold: true },
                                            { text: admissionInfo.formattedAdmissionData }
                                        ]
                                    },
                                    admissionInfo.department ? {
                                        text: [
                                            { text: 'Department: ', bold: true },
                                            { text: admissionInfo.department.name }
                                        ]
                                    } : {},
                                    {
                                        text: [
                                            { text: 'Bed / Cabin No: ', bold: true },
                                            { text: admissionInfo.service_resource ? admissionInfo.service_resource.name : '' }
                                        ]
                                    },
                                    {
                                        text: [
                                            { text: 'Date of Discharge: ', bold: true },
                                            { text: formatDate(new Date(formData.dischargeDate)) }
                                        ]
                                    }
                                ]
                            }
                        ]
                    ]
                },
            
                layout: {
                    hLineWidth: function (i, node) {
                        return (i === 0 || i === node.table.body.length) ? 1 : 0;
                    },
                    vLineWidth: function (i, node) {
                        return (i === 0 || i === node.table.widths.length) ? 1 : 0;
                    },
                    hLineColor: function () { return '#000'; },
                    vLineColor: function () { return '#000'; },
                
                    paddingLeft: () => 8,
                    paddingRight: () => 8,
                    paddingTop: () => 4,
                    paddingBottom: () => 4
                }
            },

            getDischargeMode(dischargeModes, formData),

            getDoctors(admissionInfo, treatedByDoctors),

            ...( notesOrder.investigations.length ? pushIntoTable(doc, notesOrder.investigations) : [] ),

            getOtNotes(notesOrder.otNotes),

            getBabyNotes(notesOrder.babyNotes),

            {
                text: 'Discharge Medications',
                fontSize: 12,
                bold: true,
                decoration: 'underline',
                margin: [0, 5]
            },

            getRx(dischargeNotes.rx.selectedNotes),

            getFollowUp(dischargeNotes.follow_up.selectedNotes),

            getInvestigation(dischargeNotes.investigation.selectedNotes),

            getDisChargeNotes(dischargeNotes.discharge_note.selectedNotes),
        ]
    }

    const getDischargeMode = (dischargeModes, formData) => {
        const singleMode = dischargeModes.find(item => item.value === formData.mode_of_discharge);
        if(! singleMode) return '';

        return {
            text: 'Type of Discharge: ' + singleMode.name,
            fontSize: 14,
            alignment: 'center',
            bold: true,
            color: 'black',
            margin: [0, 10, 20, 0]
        }
    }

    const pushIntoTable = (doc, investigations) => {
        const tableTitle = getInvestigationTitle();
        const tableHeader = getTableHeader()
        
        const content = {
            style: 'tableExample',
            margin: [0, 5],
            table: {
                widths: ['*', '15%', '15%', '15%', '15%', '15%'],
                body: []
            },
            layout: {
                fillColor: function (rowIndex, node, columnIndex) {
                  return '';
                },
                hLineWidth: function (i, node) {
                  return 0.5;
                },
                vLineWidth: function (i, node) {
                  return 0;
                },
                hLineColor: function (i, node) {
                  return ''; 
                },
                vLineColor: function (i, node) {
                  return (i === 0 || i === node.table.widths.length) ? '#000000' : '#dddddd';
                },
            }
        }

        content.table.body.push(tableHeader);

        for(const item of investigations){
            content.table.body.push(getRowData(item))
        }

        return [
            tableTitle,
            content
        ];
    }

    const getInvestigationTitle = () => {
        return {
            width: "50%",
            text: 'Investigation Details',
            fontSize: 12,
            bold: true,
            decoration: 'underline',
            margin: [0, 5, 0, 0]
        }
    }

    const getRowData = (item) => {
        return [ 
            { text: item.latestResult.product_name || item.oldResult.product_name },
            { text: item.latestResult.name || item.oldResult.name  },
            { text: item.latestResult.unit || item.oldResult.unit },
            { text: item.oldResult.result },
            { text: item.otherResults.length ? item.otherResults[0].result : '' },
            { text: item.otherResults.length > 1 ? last(item.otherResults).result : '' } 
        ];
    }

    const getTableHeader = () => {
        return [
            { text: 'Name of investigation', bold: true },
            { text: 'Investigation Head', bold: true },
            { text: 'Unit', bold: true },
            { text: 'Previous Result', bold: true },
            { text: 'After Admission', bold: true },
            { text: 'During Discharge', bold: true }
        ];
    }

    const getRx = (notes) => {

        if(notes.length < 1) return '';

        return {
            margin: [5, 0],
            unbreakable: true,
            stack: [
                {
                    text: 'Rx',
                    bold: true,
                    decoration: 'underline',
                },
                {
                    margin: [0, 5],
                    ol: notes.map((el) => {
                        return `${el.name} \n ${ el.dose ? el.dose : ''} \t ${el.unit ? el.unit : ''} \t ${el.remarks ? el.remarks : ''} \t ${el.duration ? el.duration : ''}`;
                    })
                }
            ]
        };
    }

    const getFollowUp = (notes) => {

        if(notes.length < 1) return '';

        return {
            margin: [5, 0],
            unbreakable: true,
            stack: [
                {
                    text: 'OPD Follow up visit',
                    bold: true,
                    decoration: 'underline',
                },
                {
                    margin: [0, 5],
                    ol: notes
                }
            ]
        };
    }

    const getInvestigation = (notes) => {
        if(notes.length < 1) return '';

        return {
            margin: [0, 5, 0, 0],
            unbreakable: true,
            stack: [
                {
                    text: 'Investigation Required',
                    bold: true,
                    decoration: 'underline',
                },
                {
                    margin: [0, 5],
                    ol: notes.map((el) => {
                        return el.name;
                    })
                }
            ]
        }
    }

    const getBabyNotes = (babyNotes) => {

        if(babyNotes.length < 1) return '';

        const formattedBabyNotes = {
            margin: [0, 5],
            unbreakable: true,
            stack: [
                {
                    text: 'Baby Notes',
                    fontSize: 12,
                    bold: true,
                    decoration: 'underline',
                }
            ]
        };

        const chunkNotes = chunkArray(babyNotes, 2);
        const formattedNotes = [];

        for(const notes of chunkNotes){
            const newNotes = notes.map((note) => {

                return {
                    width: '50%',
                    stack: [
                        {
                            text: [
                                { text: 'Gender: ', bold: true },
                                { text: startCase(note.gender) }
                            ],
                            color: 'black'
                        },
                        {
                            text: [
                                { text: 'Weight: ', bold: true },
                                { text: note.weight }
                            ],
                            color: 'black'
                        },
                        note.apgar_score ? {
                            text: [
                                { text: 'APGAR Score: ', bold: true },
                                { text: note.apgar_score }
                            ],
                            color: 'black'
                        } : '',
                        note.notes.length ? {
                            text: [
                                { text: 'Special Note: ', bold: true },
                                { text: convertArrayToString(note.notes) }
                            ],
                            color: 'black'
                        } : ''
                    ]
                }
            })
            
            formattedNotes.push({margin: [0, 5], columns: newNotes})
        }

        formattedBabyNotes.stack.push(... formattedNotes);
        return formattedBabyNotes;
    }

    const convertArrayToString = (notes) => {
        const note = notes.map(obj => obj.note).flat();
        return joinArrayOfString(note);
    }

    const getOtNotes = (otNotes) => {
        
        if(otNotes.length < 1) return '';

        const chunkNotes = chunkArray(otNotes, 2);

        const formattedNotes = {
            margin: [0, 5],
            unbreakable: true,
            stack: [
                {
                    text: 'Breaf Operation Note:',
                    fontSize: 12,
                    bold: true,
                    decoration: 'underline',
                }
            ]
        }

        for(const notes of chunkNotes){
            const newNotes = notes.map((note) => {

                const consultants = formatOTNote(note);

                return {
                    width: '50%',
                    stack: [
                        {
                            text: [
                                { text: `OT Date & Time: `, bold: true},
                                { text: note.operation_date }
                            ],
                            color: 'black',

                        },
                        {
                            text: [
                                { text: `Name of Operation: `, bold: true},
                                { text: note.name_of_operation }
                            ],
                            color: 'black',

                        },
                        note.indication ? {
                            text: [
                                { text: `Indication: `, bold: true},
                                { text: note.indication }
                            ],
                            color: 'black',

                        } : '',
                        note.special_finding ? {
                            text: [
                                { text: `Special Finding: `, bold: true},
                                { text: note.special_finding }
                            ],
                            color: 'black',

                        } : '',
                        consultants.surgeon.length ? {
                            text: [
                                { text: `Surgeon: `, bold: true},
                                { text: joinArrayOfString(consultants.surgeon) }
                            ],
                            color: 'black',

                        } : '',
                        consultants.ass_surgeon.length ? {
                            text: [
                                { text: `Asst. Surgeon: `, bold: true},
                                { text: joinArrayOfString(consultants.ass_surgeon) }
                            ],
                            color: 'black',

                        } : '',
                        consultants.anestesiologist.length ? {
                            text: [
                                { text: `Anestesiologist: `, bold: true},
                                { text: joinArrayOfString(consultants.anestesiologist) }
                            ],
                            color: 'black',

                        } : '',
                        consultants.nurse.length ? {
                            text: [
                                { text: `Nurse: `, bold: true},
                                { text: joinArrayOfString(consultants.nurse) }
                            ],
                            color: 'black',

                        } : ''
                    ]
                }
            })

            formattedNotes.stack.push({margin: [0, 5], columns: newNotes})
        }

        return formattedNotes;
    }

    const formatOTNote = (note) => {
        const consultants = {
            surgeon: [],
            anestesiologist: [],
            ass_surgeon: [],
            nurse: [],
        };
        
        for(const item of note.ot_order_service){
            consultants[item.pivot.type].push(item.name);
        }

        return consultants;
    }

    const getDisChargeNotes = (notes) => {

        if(notes.length < 1) return '';

        return {
            margin: [0, 5],
            unbreakable: true,
            stack: [
                {
                    text: 'Discharge Note',
                    fontSize: 12,
                    bold: true,
                    decoration: 'underline',
                    margin: [0, 5]
                },
                {
                    margin: [0, 5],
                    ol: notes
                }
            ]
        }
    }

    const getDoctors = (admissionInfo, treatedByDoctors) => {
        return {
            margin: [0, 10, 0, 0],
            columns: [
                admissionInfo.human_resource
                    ? createDoctorCard('Referred Doctor', admissionInfo.human_resource.name)
                    : {},

                admissionInfo.consultant
                    ? createDoctorCard('Consultant Doctor', admissionInfo.consultant ? admissionInfo.consultant.name : '')
                    : {},

                treatedByDoctors
                    ? createDoctorCard('Treating Doctors', treatedByDoctors)
                    : {}
            ]
        };
    };

    const createDoctorCard = (label, value) => {
        return {
            width: "33%",
            margin: [0, 0, 10, 0],
            stack: [
                {
                    table: {
                        widths: ['*'],
                        body: [
                            [{
                                stack: [
                                    {
                                        text: label,
                                        fontSize: 12,
                                        bold: true,
                                        decoration: 'underline',
                                        margin: [0, 0, 0, 4]
                                    },
                                    {
                                        text: value || '',
                                    }
                                ],
                                fillColor: '#f9f9f9',
                            }]
                        ]
                    },
                    layout: {
                        hLineWidth: () => 0.5,
                        vLineWidth: () => 0.5,
                        hLineColor: () => '#000',
                        vLineColor: () => '#000',
                        paddingLeft: () => 8,
                        paddingRight: () => 8,
                        paddingTop: () => 4,
                        paddingBottom: () => 4
                    }
                }
            ]
        };
    };

    const getFooter = (userName, qrcode, customizationFields, margins) => {
        const showCoverPhoto = customizationFields['extra_settings_footer_photo_upload'] && customizationFields['extra_settings_footer_photo_upload'].is_active;

        if (showCoverPhoto) {
            return {
                columns: [
                    {
                        image: customizationFields['extra_settings_footer_photo_upload'].value64,
                        fit: [600, (margins && margins.bottom) ? margins.bottom : 120],
                        alignment: 'center',
                    }
                ]
            };
        }

        return {
            margin: margins ? [margins.left, 0, margins.right, 10] : [60, 0, 60, 10],
            columns: [
                {
                    columns: [
                       shouldInclude(customizationFields,'footer_qrcode') ? {
                            svg: qrcode
                        } : '',

                        shouldInclude(customizationFields,'footer_note') ? {
                            width: '90%',
                            alignment: 'left',
                            text: 'N.B: This is a system generated documents and requires no manual signature.',
                            margin: [5, 53, 0, 0],
                            fontSize: 8
                        } : ''
                    ]
                },

                shouldInclude(customizationFields,'footer_printed_by') ? {
                    width: '20%',
                    alignment: 'right',
                    fontSize: 8,
                    margin: [0, 33, 0, 0],
                    stack: [
                        {
                            text: 'Printed by',
                            bold: true
                        },
                        {
                            text: userName
                        },
                        {
                            text: formatDate(new Date())
                        }
                    ]
                } : ''
            ]
        }
    }

    const getHeader = (company, customizationFields, margins) => {
        const showCoverPhoto = customizationFields['extra_settings_cover_photo_upload'] && customizationFields['extra_settings_cover_photo_upload'].is_active;

        // If cover photo is enabled, show only that and nothing else
        if (showCoverPhoto) {
            return {
                columns: [
                    {
                        image: customizationFields['extra_settings_cover_photo_upload'].value64,
                        fit: [600, (margins && margins.top) ? margins.top : 120],
                        alignment: 'center',
                    }
                ]
            };
        }

        // Otherwise, show normal header fields
        return {
            margin: margins ? [margins.left, 0, margins.right, 10] : [60, 0, 60, 10],
            columns: [
                shouldInclude(customizationFields, 'heading_left_logo') && company.logo64
                    ? {
                        alignment: 'left',
                        image: company.logo64,
                        maxHeight: 60
                    }
                    : null,
                {
                    alignment: 'right',
                    stack: [
                        shouldInclude(customizationFields, 'heading_right_company_name') && company.name ? company.name : '',
                        shouldInclude(customizationFields, 'heading_right_company_address') && company.address ? 'Address: ' + company.address : '',
                        shouldInclude(customizationFields, 'heading_right_company_phone') && company.phone ? 'Phone: ' + company.phone : '',
                        shouldInclude(customizationFields, 'heading_right_company_email') && company.email ? 'E-mail: ' + company.email : '',
                        shouldInclude(customizationFields, 'heading_right_vat_reg_number') && company.vat_no ? 'VAT Reg No: ' + company.vat_no : '',
                    ]
                }
            ].filter(Boolean)
        };
    };

    const getFooterTop = (serviceDoctorOrder) => {
        return {
            id: 'footerSection',
            alignment: 'justify',
            columns: [
                serviceDoctorOrder && serviceDoctorOrder.composed_service_name ? {
                    margin: [0, 40, 0, 0],
                    stack: [
                        {
                            text: 'Prepared By: ',
                            fontSize: 12,
                            bold: true,
                            decoration: 'underline',
                            alignment: 'left',
                            width: '50%'
                        },
                        {
                            text: serviceDoctorOrder.composed_service_name,
                            alignment: 'left',
                            width: '50%'
                        }
                    ]
                } : {},

                serviceDoctorOrder && serviceDoctorOrder.checked_service_name ? {
                    margin: [0, 40, 0, 0],
                    alignment: 'right',
                    stack: [
                        {
                            text: 'Checked By: ',
                            fontSize: 12,
                            bold: true,
                            decoration: 'underline',
                            alignment: 'left'
                        },
                        {
                            text: serviceDoctorOrder.checked_service_name,
                            alignment: 'left'
                        }
                    ]
                } : {},
            ]
        }
    }

    const shouldInclude = (customizationFields, key) => Object.keys(customizationFields).length === 0 || customizationFields[key];

    return {
        exportToPDF
    }
}

export default pdfPrinter;
